import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Table, Button, Modal, Form, Tab, Nav } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useGetUserDetailMutation, useAdjustCreditMutation } from '../../../api/userDetailAPISlices';
import Toaster from '../../../components/utility/Toaster';

const UsersDetail = () => {
    const { userId } = useParams();
    const token = JSON.parse(localStorage.getItem('token'));
    const currentUser = JSON.parse(localStorage.getItem('user'));

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showAdjust, setShowAdjust] = useState(false);
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustNote, setAdjustNote] = useState('');
    const [adjustType, setAdjustType] = useState('credit');
    const [adjustLoading, setAdjustLoading] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toasterMsg, setToasterMsg] = useState('');
    const [toasterStatus, setToasterStatus] = useState('');
    const [leadsPage, setLeadsPage] = useState(1);
    const [txPage, setTxPage] = useState(1);
    const PAGE_SIZE = 15;

    const [getUserDetail] = useGetUserDetailMutation();
    const [adjustCredit] = useAdjustCreditMutation();

    const loadData = async () => {
        setIsLoading(true);
        try {
            const res = await getUserDetail({ userId, token }).unwrap();
            setData(res);
        } catch (err) {
            setToasterMsg(err?.data?.message || 'Failed to load user details');
            setToasterStatus('error');
            setShowToaster(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [userId]);

    const handleAdjust = async () => {
        if (!adjustAmount || isNaN(adjustAmount) || parseFloat(adjustAmount) === 0) return;
        setAdjustLoading(true);
        const amount = adjustType === 'debit' ? -Math.abs(parseFloat(adjustAmount)) : Math.abs(parseFloat(adjustAmount));
        try {
            const res = await adjustCredit({
                userId,
                token,
                payload: { amount, note: adjustNote },
            }).unwrap();
            setToasterMsg(`Balance adjusted: $${res.balance_before.toFixed(2)} → $${res.balance_after.toFixed(2)}`);
            setToasterStatus('success');
            setShowToaster(true);
            setShowAdjust(false);
            setAdjustAmount('');
            setAdjustNote('');
            loadData();
        } catch (err) {
            setToasterMsg(err?.data?.message || 'Adjustment failed');
            setToasterStatus('error');
            setShowToaster(true);
        } finally {
            setAdjustLoading(false);
        }
    };

    const Pagination = ({ page, setPage, total }) => {
        const lastPage = Math.ceil(total / PAGE_SIZE);
        if (lastPage <= 1) return null;
        return (
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top" style={{ fontSize: 13 }}>
                <span className="text-muted">
                    Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                </span>
                <div className="d-flex gap-1">
                    <Button size="sm" variant="outline-secondary" disabled={page === 1} onClick={() => setPage(1)}>«</Button>
                    <Button size="sm" variant="outline-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</Button>
                    {Array.from({ length: lastPage }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === lastPage || Math.abs(p - page) <= 1)
                        .reduce((acc, p, i, arr) => {
                            if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                            acc.push(p);
                            return acc;
                        }, [])
                        .map((p, i) => p === '...' ? (
                            <span key={`ellipsis-${i}`} className="px-2 py-1 text-muted">…</span>
                        ) : (
                            <Button key={p} size="sm"
                                variant={p === page ? 'warning' : 'outline-secondary'}
                                onClick={() => setPage(p)}>{p}</Button>
                        ))
                    }
                    <Button size="sm" variant="outline-secondary" disabled={page === lastPage} onClick={() => setPage(p => p + 1)}>›</Button>
                    <Button size="sm" variant="outline-secondary" disabled={page === lastPage} onClick={() => setPage(lastPage)}>»</Button>
                </div>
            </div>
        );
    };

    if (isLoading) return (
        <div className="text-center mt-5 pt-5">
            <div className="spinner-border text-warning" role="status" />
        </div>
    );

    if (!data) return <div className="text-center mt-5">User not found</div>;

    const { user, leads, transactions, sources, stats } = data;
    const pagedLeads = leads.slice((leadsPage - 1) * PAGE_SIZE, leadsPage * PAGE_SIZE);
    const pagedTx = transactions.slice((txPage - 1) * PAGE_SIZE, txPage * PAGE_SIZE);

    return (
        <>
            <Container fluid>
                {/* Header */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-3">
                                {user.photo
                                    ? <img src={user.photo} alt="" className="rounded-circle" style={{ width: 56, height: 56, objectFit: 'cover' }} />
                                    : <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center" style={{ width: 56, height: 56, fontSize: 22, fontWeight: 700, color: '#fff' }}>
                                        {user.first_name?.[0]}{user.last_name?.[0]}
                                      </div>
                                }
                                <div>
                                    <h4 className="mb-0 font-weight-bold">{user.first_name} {user.last_name}</h4>
                                    <span className="text-muted" style={{ fontSize: 13 }}>{user.email}</span>
                                </div>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                <Link to={`/users-edit/${userId}`} className="btn btn-outline-secondary btn-sm">Edit User</Link>
                                {currentUser?.role === 'admin' && (
                                    <Button variant="outline-primary" size="sm" onClick={() => setShowAdjust(true)}>
                                        Adjust Balance
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Stats Cards */}
                <Row className="mb-4">
                    {[
                        { label: 'Credit Balance', value: `$${parseFloat(user.credit_balance || 0).toFixed(2)}`, color: 'text-success', icon: '💳' },
                        { label: 'Total Leads', value: stats.total_leads, color: 'text-primary', icon: '📋' },
                        { label: 'Total Spent', value: `$${parseFloat(stats.total_spent || 0).toFixed(2)}`, color: 'text-danger', icon: '💸' },
                        { label: 'Quotes Generated', value: stats.leads_with_quotes, color: 'text-info', icon: '📄' },
                    ].map((s, i) => (
                        <Col key={i} md={3} sm={6} className="mb-3">
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body className="d-flex align-items-center gap-3">
                                    <div style={{ fontSize: 32 }}>{s.icon}</div>
                                    <div>
                                        <div className={`font-weight-bold ${s.color}`} style={{ fontSize: 22 }}>{s.value}</div>
                                        <div className="text-muted" style={{ fontSize: 12 }}>{s.label}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Tabs */}
                <Tab.Container defaultActiveKey="leads">
                    <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white border-bottom-0 pt-3 px-4">
                            <Nav variant="tabs" className="border-bottom-0">
                                <Nav.Item><Nav.Link eventKey="leads">Leads ({stats.total_leads})</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="transactions">Transactions ({transactions.length})</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="settings">Settings & Sources</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="profile">Profile</Nav.Link></Nav.Item>
                            </Nav>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Tab.Content>
                                {/* Leads Tab */}
                                <Tab.Pane eventKey="leads">
                                    <div className="table-responsive">
                                        <Table className="mb-0 data-table">
                                            <thead className="table-color-heading">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Name</th>
                                                    <th>Property</th>
                                                    <th>Source</th>
                                                    <th>Cost</th>
                                                    <th>Quote</th>
                                                    <th>Date</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pagedLeads.length === 0 ? (
                                                    <tr><td colSpan={8} className="text-center py-4 text-muted">No leads yet</td></tr>
                                                ) : pagedLeads.map((lead) => (
                                                    <tr key={lead.id}>
                                                        <td className="text-muted" style={{ fontSize: 12 }}>{lead.id}</td>
                                                        <td className="font-weight-bold">{lead.first_name} {lead.last_name}</td>
                                                        <td>
                                                            <div style={{ fontSize: 13 }}>{lead.property_address}</div>
                                                            <div className="text-muted" style={{ fontSize: 11 }}>{lead.property_city}{lead.property_city && lead.property_state ? ', ' : ''}{lead.property_state}</div>
                                                        </td>
                                                        <td>
                                                            <Badge bg="secondary" style={{ fontSize: 11 }}>{lead.source || 'Unknown'}</Badge>
                                                        </td>
                                                        <td>
                                                            <span className={parseFloat(lead.cost) > 0 ? 'text-danger font-weight-bold' : 'text-muted'}>
                                                                ${parseFloat(lead.cost || 0).toFixed(2)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {lead.has_quote
                                                                ? <Badge bg="success">Generated</Badge>
                                                                : <Badge bg="warning" text="dark">Pending</Badge>
                                                            }
                                                        </td>
                                                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                            {new Date(lead.created_at).toLocaleDateString()}
                                                        </td>
                                                        <td>
                                                            <Link to={`/lead-view/${lead.id}`} className="btn btn-sm btn-outline-secondary">View</Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        <Pagination page={leadsPage} setPage={setLeadsPage} total={leads.length} />
                                    </div>
                                </Tab.Pane>

                                {/* Transactions Tab */}
                                <Tab.Pane eventKey="transactions">
                                    <div className="table-responsive">
                                        <Table className="mb-0 data-table">
                                            <thead className="table-color-heading">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Amount</th>
                                                    <th>Type</th>
                                                    <th>Stripe Intent</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pagedTx.length === 0 ? (
                                                    <tr><td colSpan={5} className="text-center py-4 text-muted">No transactions yet</td></tr>
                                                ) : pagedTx.map((t) => (
                                                    <tr key={t.id}>
                                                        <td className="text-muted" style={{ fontSize: 12 }}>{t.id}</td>
                                                        <td>
                                                            <span className={parseFloat(t.amount) >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>
                                                                {parseFloat(t.amount) >= 0 ? '+' : ''}${parseFloat(t.amount).toFixed(2)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Badge bg={parseFloat(t.amount) >= 0 ? 'success' : 'danger'}>
                                                                {parseFloat(t.amount) >= 0 ? 'Credit' : 'Debit'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <span className="text-muted" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                                                                {t.payment_intent_id || '—'}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                            {new Date(t.created_at).toLocaleDateString()}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        <Pagination page={txPage} setPage={setTxPage} total={transactions.length} />
                                    </div>
                                </Tab.Pane>

                                {/* Settings & Sources Tab */}
                                <Tab.Pane eventKey="settings">
                                    <div className="p-4">
                                        <Row>
                                            <Col md={6}>
                                                <h6 className="font-weight-bold mb-3">Billing Settings</h6>
                                                <Table borderless size="sm">
                                                    <tbody>
                                                        <tr>
                                                            <td className="text-muted">Cost Per Lead</td>
                                                            <td className="font-weight-bold">${parseFloat(user.cost_per_lead || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Credit Balance</td>
                                                            <td className="font-weight-bold text-success">${parseFloat(user.credit_balance || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Min Credit Threshold</td>
                                                            <td>${parseFloat(user.minimum_credit_threshold || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Auto Refill Amount</td>
                                                            <td>${parseFloat(user.auto_refill_amount || 0).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Quote Template</td>
                                                            <td><Badge bg="info">{user.quote_template || 'template-2'}</Badge></td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Daily Default Lead Cap</td>
                                                            <td>{user.daily_default_lead_cap || 'Unlimited'}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Col>
                                            <Col md={6}>
                                                <h6 className="font-weight-bold mb-3">GHL Integration</h6>
                                                <Table borderless size="sm">
                                                    <tbody>
                                                        <tr>
                                                            <td className="text-muted">GHL Location ID</td>
                                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{user.ghl_location_id || '—'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">GHL User ID</td>
                                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{user.ghl_user_id || '—'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Stripe Customer ID</td>
                                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{user.stripe_customer_id || '—'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Licensed States</td>
                                                            <td>
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {user.licensed_states?.length > 0
                                                                        ? user.licensed_states.map(s => <Badge key={s} bg="light" text="dark" className="border">{s}</Badge>)
                                                                        : <span className="text-muted">None</span>
                                                                    }
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </Table>


                                            </Col>
                                        </Row>
                                    </div>
                                </Tab.Pane>

                                {/* Profile Tab */}
                                <Tab.Pane eventKey="profile">
                                    <div className="p-4">
                                        <Row>
                                            <Col md={4} className="text-center mb-4">
                                                {user.photo
                                                    ? <img src={user.photo} alt="" className="rounded-circle mb-3 shadow" style={{ width: 100, height: 100, objectFit: 'cover' }} />
                                                    : <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: 100, height: 100, fontSize: 36, fontWeight: 700, color: '#fff' }}>
                                                        {user.first_name?.[0]}{user.last_name?.[0]}
                                                      </div>
                                                }
                                                <h5 className="font-weight-bold mb-1">{user.first_name} {user.last_name}</h5>
                                                <Badge bg={user.role === 'admin' ? 'danger' : user.role === 'manager' ? 'warning' : 'primary'} className="mb-1">{user.role}</Badge>
                                                <br />
                                                <Badge bg={user.is_active ? 'success' : 'secondary'}>{user.is_active ? 'Active' : 'Inactive'}</Badge>
                                            </Col>
                                            <Col md={8}>
                                                <Table borderless size="sm">
                                                    <tbody>
                                                        {[
                                                            ['Login Email', user.email],
                                                            ['Additional Email', user.additional_email || '—'],
                                                            ['Phone', user.phone || '—'],
                                                            ['NMLS', user.nmls || '—'],
                                                            ['Company', user.company_name || '—'],
                                                            ['Company NMLS', user.company_nmls || '—'],
                                                            ['Processing Fee', user.default_processing_fee ? `$${user.default_processing_fee}` : '—'],
                                                            ['Underwriting Fee', user.underwriting_fee ? `$${user.underwriting_fee}` : '—'],
                                                            ['Lender Paid Comp', user.lender_paid_compensation ? `${user.lender_paid_compensation}%` : '—'],
                                                            ['Can Shop For Fee', user.default_services_you_can_shop_for_fee ? `$${user.default_services_you_can_shop_for_fee}` : '—'],
                                                            ["Can't Shop For Fee", user.default_services_you_cannot_shop_for_fee ? `$${user.default_services_you_cannot_shop_for_fee}` : '—'],
                                                        ].map(([label, val]) => (
                                                            <tr key={label}>
                                                                <td className="text-muted" style={{ width: '40%' }}>{label}</td>
                                                                <td className="font-weight-bold">{val}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        </Row>
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </Card.Body>
                    </Card>
                </Tab.Container>
            </Container>

            {/* Adjust Balance Modal */}
            <Modal show={showAdjust} onHide={() => setShowAdjust(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Adjust Balance — {user.first_name} {user.last_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
                        <span className="text-muted">Current Balance</span>
                        <span className="font-weight-bold text-success" style={{ fontSize: 20 }}>
                            ${parseFloat(user.credit_balance || 0).toFixed(2)}
                        </span>
                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label>Type</Form.Label>
                        <div className="d-flex gap-3">
                            <Form.Check type="radio" label="Credit (add funds)" id="type-credit" name="adjustType"
                                checked={adjustType === 'credit'} onChange={() => setAdjustType('credit')} />
                            <Form.Check type="radio" label="Debit (remove funds)" id="type-debit" name="adjustType"
                                checked={adjustType === 'debit'} onChange={() => setAdjustType('debit')} />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Amount ($)</Form.Label>
                        <Form.Control type="number" min="0.01" step="0.01" placeholder="0.00"
                            value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Note (optional)</Form.Label>
                        <Form.Control type="text" placeholder="Reason for adjustment"
                            value={adjustNote} onChange={e => setAdjustNote(e.target.value)} />
                    </Form.Group>
                    {adjustAmount && !isNaN(adjustAmount) && (
                        <div className="mt-3 p-2 rounded bg-light text-center" style={{ fontSize: 13 }}>
                            New balance: <strong className="text-success">
                                ${(parseFloat(user.credit_balance || 0) + (adjustType === 'debit' ? -Math.abs(parseFloat(adjustAmount)) : Math.abs(parseFloat(adjustAmount)))).toFixed(2)}
                            </strong>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowAdjust(false)}>Cancel</Button>
                    <Button
                        variant={adjustType === 'debit' ? 'danger' : 'success'}
                        onClick={handleAdjust}
                        disabled={adjustLoading || !adjustAmount}
                    >
                        {adjustLoading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                        {adjustType === 'debit' ? 'Debit' : 'Credit'} ${adjustAmount || '0.00'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Toaster status={toasterStatus} message={toasterMsg} show={showToaster} setShow={setShowToaster} />
        </>
    );
};

export default UsersDetail;
